import { buildAuthHeaders } from "@/utils/impersonation";
import { captureNetworkError } from "@/utils/sentry";

export function getApiUrl() {
  const apiUrl = process.env.VUE_APP_API_URL;
  if (!apiUrl) {
    throw new Error("Missing VUE_APP_API_URL");
  }
  return apiUrl;
}

export function buildApiUrl(path) {
  return `${getApiUrl()}${path}`;
}

export async function parseJsonResponse(response) {
  return response.json().catch(() => ({}));
}

export function extractErrorDetail(detail) {
  if (typeof detail === "string") {
    return detail;
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return typeof detail.message === "string" ? detail.message : "";
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.detail)
      .filter(Boolean)
      .join(". ");
  }
  return "";
}

export function extractErrorCode(detail) {
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return typeof detail.code === "string" ? detail.code : "";
  }
  return "";
}

export async function readJsonOrThrow(response, fallbackMessage) {
  const payload = await parseJsonResponse(response);
  if (!response.ok) {
    const detailMessage = extractErrorDetail(payload?.detail);
    const error = new Error(detailMessage || response.statusText || fallbackMessage);
    error.code = extractErrorCode(payload?.detail);
    throw error;
  }
  return payload;
}

export async function apiRequest(
  path,
  {
    method = "GET",
    session = null,
    body,
    headers = {},
    includeContentType = body !== undefined,
  } = {}
) {
  const requestHeaders = session
    ? {
        ...buildAuthHeaders(session, { includeContentType }),
        ...headers,
      }
    : {
        ...(includeContentType ? { "Content-Type": "application/json" } : {}),
        ...headers,
      };

  const url = buildApiUrl(path);
  try {
    return await fetch(url, {
      method,
      headers: requestHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (error && typeof error === "object" && !error.code) {
      error.code = "network_error";
    }
    // Fallo de red sin respuesta HTTP (el "Failed to fetch"): lo reportamos a
    // Sentry con contexto y re-lanzamos para no cambiar el comportamiento.
    captureNetworkError(error, { method, url });
    throw error;
  }
}
